import type { MutableRefObject } from "react";

import type { QueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { getLocalStorageCountry, getUserFacingApiErrorMessage } from "utils";

import { ALLOWED_NAME_CHARS_REGEX, COUNTRY_CODE, GET_SOIL_SIGHT_SCENARIO, MAX_NAME_LENGTH, MIN_NAME_LENGTH, SOIL_SIGHT_SAVE_IDLE_TIMEOUT_MS } from "consts";

import type {
  CreateSoilSightProjectBody,
  CreateSoilSightProjectResponse,
  SoilSightProject,
  SoilSightProjectResponse,
  SoilSightScenario,
  SoilSightScenarioDetail,
} from "models";

import { Message } from "components";

import type { ForecastPrimaryAction } from "pages/app/scenario-analysis/components/ForecastSettingsBar";
import type { ScenarioAnalysisTab } from "pages/app/scenario-analysis/components/ScenarioAnalysisTabBar";
import { type InvalidYearIdsByScenario, type ScenarioData, type SettingsPayloadInput } from "pages/app/scenario-analysis/types/scenario";
import { pruneEmptyCropYears } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { fromApiScenario, fromApiSettingsRefinements, toApiCropYear, toApiSettingsRefinements } from "pages/app/scenario-analysis/utils/scenarioApiMappers";
import { getReferenceScenario, referenceYearsAreComplete } from "pages/app/scenario-analysis/utils/scenarioValidation";

const escapeRegex = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const getNameValidationError = (value: string, t: (key: string) => string, existingNames?: Set<string>): string | null => {
  const trimmed = value.trim();
  if (value !== value.trimStart()) return t("nameValidation.noLeadingSpaces");
  if (trimmed.length < MIN_NAME_LENGTH) return t("nameValidation.tooShort");
  if (trimmed.length > MAX_NAME_LENGTH) return t("nameValidation.tooLong");
  if (!ALLOWED_NAME_CHARS_REGEX.test(trimmed)) return t("nameValidation.invalidCharacters");
  if (!/[a-zA-Z]/.test(trimmed)) return t("nameValidation.noLetter");
  if (existingNames && existingNames.has(trimmed.toLowerCase())) return t("nameValidation.duplicateName");
  return null;
};

/**
 * Returns the next available "Project N" name in the current locale, filling gaps left by deletions.
 * `translate` should be `(n: number) => t("projectManagement.defaultName", "Project {{n}}", { n })`.
 */
export const getNextSoilSightProjectName = (projects: Pick<SoilSightProject, "name">[], translate: (n: number) => string): string => {
  // Derive locale-specific prefix/suffix by rendering with n=0 and splitting on the literal "0".
  // Handles both suffix-number ("Project 0") and prefix-number ("0 Projekt") locales.
  const rendered0 = translate(0);
  const zeroIdx = rendered0.indexOf("0");
  const prefix = zeroIdx > 0 ? rendered0.slice(0, zeroIdx).trimEnd() : "";
  const suffix = zeroIdx >= 0 ? rendered0.slice(zeroIdx + 1).trimStart() : "";

  const used = new Set<number>();
  if (zeroIdx >= 0) {
    const re = new RegExp(`^${escapeRegex(prefix)}\\s*(\\d+)\\s*${escapeRegex(suffix)}$`, "i");
    for (const f of projects) {
      const m = f.name?.match(re);
      if (m) used.add(parseInt(m[1], 10));
    }
  }
  let n = 1;
  while (used.has(n)) n += 1;
  return translate(n);
};

export const areReferenceCropYearsSaved = (scenarios: ScenarioData[], savedCropYears: Map<string, string>): boolean => {
  const reference = getReferenceScenario(scenarios);
  if (!reference) return false;
  const lastSavedSerializedPayload = savedCropYears.get(reference.id);
  if (!lastSavedSerializedPayload) return false;
  const currentSerializedPayload = JSON.stringify(toCropYearsPayload(reference.years));
  return currentSerializedPayload === lastSavedSerializedPayload;
};

export type AutosaveRefs = {
  hasHydratedRef: MutableRefObject<boolean>;
  cropYearsSavedRef: MutableRefObject<Map<string, string>>;
  scenarioMetaSavedRef: MutableRefObject<Map<string, string>>;
  settingsSavedRef: MutableRefObject<string | null>;
};

export type HydrationSeedArgs = {
  hydrated: SettingsPayloadInput;
  hydratedScenarios: ScenarioData[];
  summaries: Array<{ id: string; name: string }>;
  cropYearsSavedRef: MutableRefObject<Map<string, string>>;
  scenarioMetaSavedRef: MutableRefObject<Map<string, string>>;
  settingsSavedRef: MutableRefObject<string | null>;
};

/**
 * Creates a Soil Sight project and optionally assigns the largest available field to it.
 * Returns the new `soilSightProjectId`, or `null` if creation failed.
 */
export const createSoilSightProjectWithDefaults = async ({
  body,
  fields,
  preferredFieldId,
  create,
  assignField,
}: {
  body: CreateSoilSightProjectBody;
  /** Pass the farm's field list to assign the largest field after creation. Pass [] to skip. */
  fields: Array<{ id: string; size: number }>;
  /** Assigned instead of the largest field, e.g. the field an import came from. */
  preferredFieldId?: string | null;
  create: (body: CreateSoilSightProjectBody) => Promise<CreateSoilSightProjectResponse | null | undefined>;
  assignField: (args: { soilSightProjectId: string; body: { fieldId: string } }) => Promise<unknown>;
}): Promise<string | null> => {
  const created = await create(body);
  const soilSightProjectId = created?.soilSightProjectId ?? null;
  const largestField = fields.reduce<{ id: string; size: number } | null>((best, f) => (!best || f.size > best.size ? { id: f.id, size: f.size } : best), null);
  const fieldId = preferredFieldId || largestField?.id;
  if (soilSightProjectId && fieldId) {
    await assignField({ soilSightProjectId, body: { fieldId } });
  }
  return soilSightProjectId;
};

export const showSoilSightSaveFailed = (t: (key: string) => string, error?: unknown): void => {
  Message({
    content: getUserFacingApiErrorMessage(error, t("outcomeComparison.soilSightsSaveFailed")),
    icon: "error",
    dataTestId: "error-soil-sight-save-failed",
  });
};

/**
 * Whether a failed Soil Sights recalc is a genuine device-connectivity problem (show the "offline"
 * message) rather than a server/downstream outage (show "temporarily unavailable"). The client can't tell
 * "our BE is down" from "PEAS/SilverService is down", so anything where the server actually answered — or
 * where the device still reports connectivity — collapses to "unavailable". navigator.onLine is trusted
 * only in its reliable "false" direction; a "true" value does not guarantee real reachability.
 */
export const isDeviceOfflineError = (error: unknown): boolean => {
  const serverAnswered = isAxiosError(error) && !!error.response;
  return !serverAnswered && !window.navigator.onLine;
};

/** Country code stored as Soil Sight `locationRegion` — must never be empty (backend enum). */
export const resolveSoilSightLocationRegion = (options?: { farmCountry?: string | null; userCountry?: string | null }): string => {
  const candidates = [options?.farmCountry, options?.userCountry, getLocalStorageCountry(), COUNTRY_CODE.GERMANY];
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) return trimmed;
  }
  return COUNTRY_CODE.GERMANY;
};

export const toCropYearsPayload = (years: ScenarioData["years"]) => ({
  cropYears: years.map((year, index) => toApiCropYear(year, index + 1)),
});

/**
 * Whether two scenarios carry the same model inputs. Identical inputs cannot produce a different
 * forecast, so there is nothing to compare — blank scaffold rows are pruned so they cannot mask a match.
 */
export const scenariosHaveIdenticalCropYears = (first: ScenarioData | null, second: ScenarioData | null): boolean => {
  if (!first || !second) return false;
  const asPayload = (scenario: ScenarioData) => JSON.stringify(toCropYearsPayload(pruneEmptyCropYears(scenario.years)));
  return asPayload(first) === asPayload(second);
};

export const resetAutosaveRefs = ({ hasHydratedRef, cropYearsSavedRef, scenarioMetaSavedRef, settingsSavedRef }: AutosaveRefs): void => {
  hasHydratedRef.current = false;
  cropYearsSavedRef.current.clear();
  scenarioMetaSavedRef.current.clear();
  settingsSavedRef.current = null;
};

export const seedAutosaveRefsFromHydration = ({
  hydrated,
  hydratedScenarios,
  summaries,
  cropYearsSavedRef,
  scenarioMetaSavedRef,
  settingsSavedRef,
}: HydrationSeedArgs): void => {
  hydratedScenarios.forEach((scenario) => {
    cropYearsSavedRef.current.set(scenario.id, JSON.stringify(toCropYearsPayload(scenario.years)));
  });

  settingsSavedRef.current = JSON.stringify(toApiSettingsRefinements(hydrated));
  summaries.forEach((scenario) => {
    scenarioMetaSavedRef.current.set(scenario.id, JSON.stringify({ name: scenario.name }));
  });
};

export type BuildInitialStateArgs = {
  projectData?: SoilSightProjectResponse;
  summaries: SoilSightScenario[];
  scenarioDetails: SoilSightScenarioDetail[];
  importedSelectedFieldId?: string | null;
  importAlreadyPersisted?: boolean;
};

export type InitialPageState = {
  hydrated: ReturnType<typeof fromApiSettingsRefinements>;
  projectRefinements: SettingsPayloadInput["projectRefinements"];
  scenarios: ScenarioData[];
  hasCalculated: boolean;
};

/** Pure mapping from server payloads (plus field-import overrides) to the page's initial hydrated state. */
export const buildInitialState = ({
  projectData,
  summaries,
  scenarioDetails,
  importedSelectedFieldId,
  importAlreadyPersisted,
}: BuildInitialStateArgs): InitialPageState => {
  const hydrated = fromApiSettingsRefinements(projectData?.settingsRefinements);
  const projectRefinements = importedSelectedFieldId
    ? { ...hydrated.projectRefinements, location: { ...hydrated.projectRefinements.location, fieldSelect: importedSelectedFieldId } }
    : hydrated.projectRefinements;

  if (summaries.length === 0) {
    return { hydrated, projectRefinements, scenarios: [], hasCalculated: false };
  }

  const detailById = new Map<string, SoilSightScenarioDetail>(scenarioDetails.map((detail) => [detail.id, detail]));
  const hydratedScenarios = summaries
    .slice()
    .sort((firstSummary, secondSummary) => (firstSummary.isReference ? 0 : 1) - (secondSummary.isReference ? 0 : 1))
    .map((summary) => fromApiScenario({ ...summary, cropYears: detailById.get(summary.id)?.cropYears }));

  const hasCalculated = referenceYearsAreComplete(hydratedScenarios) && !importAlreadyPersisted;

  return { hydrated, projectRefinements, scenarios: hydratedScenarios, hasCalculated };
};

/** Puts scenarios whose server delete failed back in their original place, keeping every other current edit. */
export const restoreFailedDeletions = (previous: ScenarioData[], current: ScenarioData[], failedIds: ReadonlySet<string>): ScenarioData[] => {
  if (failedIds.size === 0) return current;
  const currentById = new Map(current.map((scenario) => [scenario.id, scenario]));
  const previousIds = new Set(previous.map((scenario) => scenario.id));
  const restoredInOrder = previous.flatMap((scenario) => {
    const kept = currentById.get(scenario.id);
    if (kept) return [kept];
    return failedIds.has(scenario.id) ? [scenario] : [];
  });
  return [...restoredInOrder, ...current.filter((scenario) => !previousIds.has(scenario.id))];
};

export const syncScenarioDetailCache = (queryClient: QueryClient, scenario: ScenarioData): void => {
  const { cropYears } = toCropYearsPayload(scenario.years);
  queryClient.setQueryData<SoilSightScenarioDetail | undefined>([GET_SOIL_SIGHT_SCENARIO, scenario.id], (old) =>
    old ? { ...old, cropYears, name: scenario.title } : old
  );
};

//  Until POST /soil-sight/scenarios
// returns, we should not try to PUT against a client-only id.
export const isApiScenarioId = (id: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

/** Resolves true once no save is in flight, or false after the timeout (e.g. saves paused while offline). */
export const waitUntilIdle = async (countRef: MutableRefObject<number>, timeoutMs = SOIL_SIGHT_SAVE_IDLE_TIMEOUT_MS): Promise<boolean> => {
  const giveUpAt = Date.now() + timeoutMs;
  while (countRef.current > 0) {
    if (Date.now() >= giveUpAt) return false;
    // eslint-disable-next-line no-await-in-loop
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 50);
    });
  }
  return true;
};

/** Year ids a blocked recalculation highlights, per scenario, by the field each is missing; undefined when nothing is missing. */
export const getRecalcValidationErrors = (scenarios: ScenarioData[]): InvalidYearIdsByScenario | undefined => {
  const errors: InvalidYearIdsByScenario = new Map();
  for (const scenario of scenarios) {
    const cropYearIds = scenario.years.filter((year) => !year.crop).map((year) => year.id);
    const tillageYearIds = scenario.years.filter((year) => year.crop && !year.tillageId).map((year) => year.id);
    const residueYearIds = scenario.years.filter((year) => year.crop && year.residue == null).map((year) => year.id);
    if (cropYearIds.length || tillageYearIds.length || residueYearIds.length) {
      errors.set(scenario.id, { cropYearIds, tillageYearIds, residueYearIds });
    }
  }
  return errors.size > 0 ? errors : undefined;
};

type ForecastPrimaryActionState = {
  activeTab: ScenarioAnalysisTab;
  isSettingsRunPending: boolean;
  isRecalculating: boolean;
  isDirty: boolean;
  hasCalculated: boolean;
  hasOutcomesData: boolean;
};

/** The settings bar's action: a settings pick shows its own run, other runs show nothing, stale results offer a recalculation. */
export const getForecastPrimaryAction = ({
  activeTab,
  isSettingsRunPending,
  isRecalculating,
  isDirty,
  hasCalculated,
  hasOutcomesData,
}: ForecastPrimaryActionState): ForecastPrimaryAction | null => {
  if (activeTab === "scenarios") return null;
  if (isSettingsRunPending) return "calculating";
  if (isRecalculating) return null;
  if (isDirty || !hasCalculated || !hasOutcomesData) return "recalculate";
  return null;
};

type PageLoadStateInput = {
  soilSightProjectId: string | null;
  soilSightCacheReady: boolean;
  hydrationApplied: boolean;
  projectQuery: { isError: boolean; isLoading: boolean; isSuccess: boolean };
  scenarioListQuery: { isError: boolean; isLoading: boolean; isSuccess: boolean };
  anyDetailLoading: boolean;
  anyDetailError: boolean;
  allDetailsResolved: boolean;
};

/** Whether the project failed to load, or is still loading (including the render before its data is applied). */
export const getPageLoadState = ({
  soilSightProjectId,
  soilSightCacheReady,
  hydrationApplied,
  projectQuery,
  scenarioListQuery,
  anyDetailLoading,
  anyDetailError,
  allDetailsResolved,
}: PageLoadStateInput): { hasHardError: boolean; isHardLoading: boolean } => {
  const hasProject = !!soilSightProjectId;
  const hasHardError = hasProject && (projectQuery.isError || scenarioListQuery.isError || anyDetailError);
  const awaitingHydration = hasProject && !hydrationApplied && projectQuery.isSuccess && scenarioListQuery.isSuccess && allDetailsResolved;
  const isHardLoading =
    hasProject && !hasHardError && (!soilSightCacheReady || projectQuery.isLoading || scenarioListQuery.isLoading || anyDetailLoading || awaitingHydration);
  return { hasHardError, isHardLoading };
};

type FieldReselectInput = {
  isProjectLoaded: boolean;
  soilSightProjectId: string | null;
  hydrationApplied: boolean;
  /** The field-import transition assigns the field asynchronously, so the prompt must not flash meanwhile. */
  isTransitionOpen: boolean;
  fieldsForProjectFarmLoaded: boolean;
  projectFieldId: string | null | undefined;
  farmFields: { id: string }[];
  projectHasScenarios: boolean;
};

/** The project's field was deleted (or never set while it has scenarios), so a run would fail server-side. */
export const isFieldReselectNeeded = ({
  isProjectLoaded,
  soilSightProjectId,
  hydrationApplied,
  isTransitionOpen,
  fieldsForProjectFarmLoaded,
  projectFieldId,
  farmFields,
  projectHasScenarios,
}: FieldReselectInput): boolean =>
  isProjectLoaded &&
  !!soilSightProjectId &&
  hydrationApplied &&
  !isTransitionOpen &&
  fieldsForProjectFarmLoaded &&
  ((!!projectFieldId && !farmFields.some((field) => field.id === projectFieldId)) || (!projectFieldId && projectHasScenarios));
